<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import FilterList from '@/onenetwork/components/filter/FilterList.vue'
import { useRouter } from 'vue-router'
import { computed, onMounted, ref, watch } from 'vue'
import _ from 'lodash'
import { useConfirm } from 'primevue'
import {
  LinkedServiceListActionTypes,
  useLinkedServiceListStore,
} from '@/modules/linkedservice/stores/linkedservice.list.store.ts'
import {
  useLinkedServiceDetailStore,
} from '@/modules/linkedservice/stores/linkedservice.detail.store.ts'
import LinkedserviceDetail from '@/modules/linkedservice/views/LinkedserviceDetail.vue'
import { useReactiveQueryParam, useUpdateUrlParam } from '@/composables/useReactiveQueryParam'

const router = useRouter()
const confirm = useConfirm()
const linkedServiceListStore = useLinkedServiceListStore()
const linkedServiceDetailStore = useLinkedServiceDetailStore()
const idsSelected = ref<Array<string | number>>([])
const pageTabs = computed(() => {
  return linkedServiceListStore.tabs
})
const displayTabContent = computed(() => {
  return linkedServiceListStore.tabs?.find((tab) => tab.active)?.id || 'list'
})
const TableConfig = computed(() => {
  return linkedServiceListStore.tableConfig
})
const filter = computed(() => {
  return linkedServiceListStore.filter
})
const loading = computed(() => {
  return linkedServiceListStore.action_type == LinkedServiceListActionTypes.FETCHING
})
const page = computed(() => {
  return linkedServiceListStore.page
})
const pageSize = computed(() => {
  return linkedServiceListStore.pageSize
})
const dataSource = computed(() => {
  return linkedServiceListStore.data
})

const activeTab = computed(() => {
  return linkedServiceListStore.tabs.find((tab) => tab.active)
})
const detailId: any = useReactiveQueryParam('detailId')
const { updateUrlParam } = useUpdateUrlParam()

const setDetailTab = async () => {
  if(parseInt(detailId.value || 0)){
    try {
      const data: any = await linkedServiceDetailStore.fetchLinkedServiceDetail(detailId.value);
      linkedServiceListStore.newTab({
        id: data.id,
        label: `#${data.id} - ${data?.name}`,
        active: true,
      })
    } catch (e: any) {
      console.log(e)
    }
  }
}

const handleChangeTab = (tab: any) => {
  linkedServiceListStore.changeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleCloseTab = (tab: any) => {
  linkedServiceListStore.closeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  linkedServiceDetailStore.removeLinkedServiceDetail(tab.id)
}

const handleCloseAllTab = () => {
  linkedServiceListStore.closeAllTab()
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  linkedServiceDetailStore.removeAllLinkedServiceDetail()
}

const handleChangePage = (page: number) => {
  linkedServiceListStore.changePage(page)
}

const handleChangePageSize = (size: number) => {
  linkedServiceListStore.changePageSize(size)
}

const handleUpdateFilter = (filter: any) => {
  linkedServiceListStore.setFilter(filter)
}

const handleRowDblClick = (e: MouseEvent, item: any) => {
  // console.log(e, item)
  linkedServiceListStore.newTab({
    id: item.code,
    label: `#${item.code} - ${item.name}`,
    active: true,
  })
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleChangeSelectedRow = (ids: Array<string | number>) => {
  idsSelected.value = ids
}

const handleSort = (field: string, direction: string) => {
  linkedServiceListStore.toggleSort(field, direction)
}

const requestFetchAccountList = _.debounce(async () => {
  try {
    await linkedServiceListStore.fetchList()
  } catch (e) {}
}, 500)

watch(
  () => linkedServiceListStore.uuid,
  (newVal, oldVal) => {
    requestFetchAccountList()
  },
)

onMounted(() => {
  linkedServiceListStore.changeTab('list')
  if (linkedServiceListStore.action_type === LinkedServiceListActionTypes.PENDING) requestFetchAccountList()
  if (detailId) setDetailTab()
})

const requestDelete = async () => {
  try {
    await linkedServiceListStore.deleteListing(idsSelected.value)
    requestFetchAccountList()
  } catch (e: any) {
    console.error('Error deleting listing:', e)
  }
}

const handleDelete = async () => {
  if (idsSelected.value.length === 0) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn xoá tin đăng này không?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestDelete()
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}
</script>

<template>
  <dashboard-layout
    currentRoute="services/list"
    page-title="Danh sách dịch vụ liên kết"
    :tabs="pageTabs"
    :tabsEvent="{
      close: handleCloseTab,
      select: handleChangeTab,
      closeAll: handleCloseAllTab,
    }"
  >
    <template v-if="displayTabContent == 'list'">
      <filter-list :filter="filter" @update:filter="handleUpdateFilter" />
      <data-table
        :loading="loading"
        :tableConfig="TableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        :buttons="[
          {
            label: 'Tạo dịch vụ mới',
            icon: 'gridicons:create',
            class: 'btn bg-primary bg-hover-primary text-white',
            action: () => {
              router.push({ name: 'liked-services-create' })
            },
            perm: 'linked_service.create',
          },
        ]"
        :row-action-buttons="[{
          action: handleRowDblClick,
          css: 'btn btn-cs',
          label: 'Chi tiết',
          icon: 'iconamoon:edit-duotone',
          perm: 'linked_service.detail',
        }]"
        primaryField="code"
        @page-changed="handleChangePage"
        @pagesize-changed="handleChangePageSize"
        @toggle-visibility="linkedServiceListStore.toggleColumnVisibility"
        @on-selected-row="handleChangeSelectedRow"
        @on-sort="handleSort"
      />
    </template>
    <template v-else>
      <template v-for="tab in pageTabs">
        <template v-if="tab.id == displayTabContent">
          <LinkedserviceDetail :id="tab.id" :key="tab.id" />
        </template>
      </template>
    </template>
  </dashboard-layout>
</template>

<style scoped></style>
